/**
 * Chrome for the two public legal documents — the EULA and the privacy notice.
 *
 * These are the only pages in the app that render without a session, and that
 * is the point of them: Intuit requires a licence agreement and a privacy
 * policy reachable at a plain URL by a reviewer who will never hold a firm
 * Google account, and so does anyone deciding whether to sign in at all. They
 * therefore sit outside `RequireAuth` and carry their own header rather than
 * the app shell, which assumes a user.
 *
 * Sections are passed as data rather than as children so the contents list and
 * the headings cannot drift apart — a legal document whose index points at a
 * clause that has been renamed is worse than one with no index.
 */
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Wordmark } from '../ui/Wordmark'

export interface LegalSection {
  /** Fragment id, so a clause can be cited as a link. */
  id: string
  title: string
  body: ReactNode
}

/**
 * Body copy is written as plain semantic HTML inside each section and styled
 * from here. Descendant selectors rather than a component per element: these
 * documents are mostly prose, and wrapping every paragraph in a component
 * makes the text harder to read in the file than it is on the screen.
 */
const PROSE = [
  '[&_p]:mt-3 [&_p]:text-sm [&_p]:leading-relaxed [&_p]:text-ink-700',
  '[&_ul]:mt-3 [&_ul]:space-y-2 [&_ul]:pl-5 [&_li]:list-disc [&_li]:text-sm',
  '[&_li]:leading-relaxed [&_li]:text-ink-700 [&_li]:marker:text-ink-400',
  '[&_h3]:mt-6 [&_h3]:font-display [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:text-brand-900',
  '[&_strong]:font-medium [&_strong]:text-ink-900',
  '[&_code]:rounded-sm [&_code]:bg-ink-100 [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono',
  '[&_code]:text-[0.8em] [&_code]:text-ink-800',
  '[&_a]:font-medium [&_a]:text-brand-700 [&_a]:underline [&_a]:decoration-brand-300',
  '[&_a]:underline-offset-2 [&_a:hover]:text-brand-900',
].join(' ')

export function LegalDocument({
  title,
  standfirst,
  effectiveDate,
  sections,
  related,
}: {
  title: string
  /** One paragraph saying what the document is, before any clause. */
  standfirst: ReactNode
  effectiveDate: string
  sections: readonly LegalSection[]
  /** The sibling document, so the two are never a dead end. */
  related: { to: string; label: string }
}) {
  return (
    <div className="min-h-full bg-ink-50">
      <header className="border-b border-ink-200 bg-white">
        <div className="mx-auto flex max-w-3xl flex-wrap items-center justify-between gap-3 px-4 py-4 sm:px-6">
          {/**
           * The mark links to sign-in rather than to `/`, which would bounce a
           * signed-out reader straight back here through the login redirect.
           */}
          <Link to="/login" className="rounded-sm focus-visible:outline-2 focus-visible:outline-brand-600">
            <Wordmark tone="dark" />
          </Link>
          <Link
            to="/login"
            className="text-sm font-medium text-brand-700 underline decoration-brand-300 underline-offset-2 hover:text-brand-900"
          >
            Sign in
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-10 sm:px-6 sm:py-14">
        <article className="rounded-lg border border-ink-200 bg-white px-5 py-8 shadow-[0_1px_2px_rgba(21,26,108,0.04)] sm:px-10">
          <h1 className="font-display text-2xl font-semibold tracking-tight text-brand-900 sm:text-3xl">
            {title}
          </h1>
          <p className="mt-2 text-xs tracking-[0.14em] text-ink-500 uppercase">
            Effective {effectiveDate}
          </p>

          <div className={`mt-6 border-b border-ink-100 pb-8 ${PROSE}`}>{standfirst}</div>

          <nav aria-label="Contents" className="mt-8">
            <h2 className="text-xs font-semibold tracking-[0.14em] text-ink-500 uppercase">
              Contents
            </h2>
            <ol className="mt-3 space-y-1.5">
              {sections.map((section, index) => (
                <li key={section.id} className="flex gap-2 text-sm">
                  {/** Outside the anchor, so the clause number is not underlined with the title. */}
                  <span className="w-5 shrink-0 text-right font-mono text-xs text-ink-500">
                    {index + 1}.
                  </span>
                  <a
                    href={`#${section.id}`}
                    className="text-brand-700 underline decoration-brand-200 underline-offset-2 hover:text-brand-900"
                  >
                    {section.title}
                  </a>
                </li>
              ))}
            </ol>
          </nav>

          <div className="mt-10 space-y-10">
            {sections.map((section, index) => (
              <section key={section.id} id={section.id} className="scroll-mt-6">
                <h2 className="font-display text-lg font-semibold text-brand-900">
                  <span className="mr-2 font-mono text-sm font-normal text-ink-500">
                    {index + 1}.
                  </span>
                  {section.title}
                </h2>
                <div className={PROSE}>{section.body}</div>
              </section>
            ))}
          </div>
        </article>

        <p className="mt-6 text-center text-sm text-ink-600">
          See also{' '}
          <Link
            to={related.to}
            className="font-medium text-brand-700 underline decoration-brand-300 underline-offset-2 hover:text-brand-900"
          >
            {related.label}
          </Link>
          .
        </p>
      </main>
    </div>
  )
}
